import type { ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArtifactActionButtons } from "@/components/artifacts/ArtifactActionButtons";
import { ArtifactKindIcon } from "@/components/artifacts/ArtifactKindIcon";
import { artifactIconVisual } from "@/components/artifacts/artifactKindVisual";
import { openArtifactDestination } from "@/components/artifacts/openArtifactDestination";
import { faUser } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { Artifact, ArtifactKind } from "@/services/api/agentic-v2";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { useOpenWindow } from "@/components/ui/os-layout/useWindow";
import {
  WORKBENCH_CARD_META_CLASS,
  WORKBENCH_CARD_META_LINK_CLASS,
  WORKBENCH_CARD_META_SEP_CLASS,
} from "@/components/ui/workbench";
import { ArtifactVersionBadge } from "@/components/artifacts/ArtifactVersionBadge";
import { Badge } from "@/components/ui/badge/Badge";

/**
 * Display one artefact (e.g. a Starter Session deck) with action buttons.
 *
 * Surfaces whatever's present in ``storage.*`` via ``ArtifactActionButtons``.
 * Card click opens the report-builder viewer in a new tab when applicable,
 * otherwise the in-OS artefact window.
 *
 * Used by the Artifacts tab on Mission Detail, the Latest Artefacts
 * section on Client Detail, Mission Control, and embedded into Task Thread
 * messages with ``content_type = "artifact_card"``.
 */
export interface ArtifactCardProps {
  artifact: Artifact;
  /** Density variant. ``compact`` is used inside task threads. */
  variant?: "default" | "compact";
  /** When set, replaces the default relative-time slot (e.g. collapsed thread timestamps). */
  timestampSlot?: ReactNode;
}

export function ArtifactCard({
  artifact,
  variant = "default",
  timestampSlot,
}: ArtifactCardProps) {
  const openWindow = useOpenWindow();
  const isCompact = variant === "compact";
  const handleOpenArtifactWindow = () => {
    openArtifactDestination(artifact, openWindow, { mode: "view" });
  };

  const hasIcon = artifactIconVisual(
    artifact.kind,
    artifact.mime_type,
    artifact.filename,
  );

  return (
    <MissionControlCard
      data-testid={`artifact-card-${artifact._id}`}
      compact={isCompact}
      onClick={handleOpenArtifactWindow}
      icon={
        hasIcon ? (
          <ArtifactKindIcon
            kind={artifact.kind}
            mimeType={artifact.mime_type}
            filename={artifact.filename}
            variant="card"
          />
        ) : undefined
      }
      title={artifact.filename}
      titleBadges={
        <>
          <ArtifactVersionBadge version={artifact.version} />
          <KindPill
            kind={artifact.kind}
            businessKind={
              (artifact.metadata as { business_kind?: string } | undefined)
                ?.business_kind
            }
          />
        </>
      }
      status={<ApprovalPill approval={artifact.approval} />}
      subtitle={<ArtifactContextLine artifact={artifact} />}
      timestamp={
        timestampSlot ?? (
          <span title={new Date(artifact.created_at).toISOString()}>
            {formatRelativeTime(artifact.created_at)}
          </span>
        )
      }
      actions={
        <ArtifactActionButtons artifact={artifact} compact={isCompact} />
      }
    />
  );
}

// ── Bits ──────────────────────────────────────────────────────────────

const KIND_LABELS: Record<string, string> = {
  advice_session_deck: "Advice charts",
  advice_session_advisor_notes: "Advisor notes",
  advice_session_charges_workbook: "Charges workbook",
  starter_session_deck: "Starter session",
  starter_session_brief: "Starter brief",
  client_narrative: "Paraplanner narrative",
  suitability_letter: "Suitability letter",
  advisor_notes: "Advisor notes",
  plan_cheat_sheet: "Cheat sheet",
  plan_fees_summary: "Fees summary",
  loa: "LOA",
  user_upload: "Upload",
  cgl_call_note: "Discovery call note",
  checklist: "Checklist",
  table: "Table",
  form: "Form",
  report: "Report",
};

const BUSINESS_KIND_LABELS: Record<string, string> = {
  loa: "LOA",
};

export function artifactKindLabel(
  kind: string,
  businessKind?: string,
): string {
  return (
    (businessKind && BUSINESS_KIND_LABELS[businessKind]) ??
    KIND_LABELS[kind] ??
    kind.replace(/_/g, " ")
  );
}

function KindPill({
  kind,
  businessKind,
}: {
  kind: ArtifactKind;
  businessKind?: string;
}) {
  return (
    <Badge color="indigo" size="sm">
      {artifactKindLabel(kind, businessKind)}
    </Badge>
  );
}

function ApprovalPill({
  approval,
}: {
  approval: Artifact["approval"];
}) {
  if (!approval) return null;
  const tone: Record<string, "green" | "red" | "amber" | "zinc"> = {
    approved: "green",
    rejected: "red",
    pending: "amber",
  };
  return (
    <Badge color={tone[approval.state] ?? "zinc"} size="sm">
      {approval.state}
    </Badge>
  );
}

/**
 * "For Bobby Sibal · via Onboard new client" line shown on cards in
 * cross-context views (Mission Control). Each chip opens its respective
 * OS app window. Hidden when the artefact has no hydrated refs (e.g.
 * inside a Client Detail page where the context is implicit).
 */
function ArtifactContextLine({ artifact }: { artifact: Artifact }) {
  const openWindow = useOpenWindow();
  const entity = artifact.entity ?? null;
  const mission = artifact.mission ?? null;
  if (!entity && !mission) return null;

  return (
    <div
      className={clsx(
        "mt-0.5 flex min-w-0 items-center gap-1 truncate",
        WORKBENCH_CARD_META_CLASS,
      )}
    >
      {mission?.name && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openWindow({
                appId: "mission",
                title: mission.name ?? "Mission",
                params: { missionId: mission.mission_id },
              });
            }}
            className={clsx("max-w-[14rem] truncate", WORKBENCH_CARD_META_LINK_CLASS)}
          >
            {mission.name}
          </button>
          {entity?.display_name && (
            <span className={clsx("shrink-0", WORKBENCH_CARD_META_SEP_CLASS)}>·</span>
          )}
        </>
      )}
      {entity?.display_name && (
        <span className="flex min-w-0 items-center gap-1 truncate">
          <FontAwesomeIcon
            icon={faUser}
            className={clsx("shrink-0 text-[10px]", WORKBENCH_CARD_META_CLASS)}
            aria-hidden
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openWindow({
                appId: "client",
                title: entity.display_name ?? "Client",
                params: { faClientId: entity.entity_id },
              });
            }}
            className={clsx("max-w-[14rem] truncate", WORKBENCH_CARD_META_LINK_CLASS)}
          >
            {entity.display_name}
          </button>
        </span>
      )}
    </div>
  );
}
